import { useEffect } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import Nav from "@/components/sections/Nav";
import Footer from "@/components/sections/Footer";
import { getProjectBySlug, PROJECTS } from "@/data/projects";
import { ArrowLeft, ArrowUpRight, MapPin, Calendar } from "lucide-react";

function Block({ block }) {
  switch (block.type) {
    case "h2":
      return (
        <h2 className="mt-14 mb-4 font-[Outfit] font-light text-[26px] sm:text-[32px] leading-[1.15] text-[#2D332B] tracking-[-0.01em]">
          {block.text}
        </h2>
      );
    case "p":
    default:
      return (
        <p className="my-5 text-[16px] sm:text-[17px] leading-[1.8] text-[#3d4238]">
          {block.text}
        </p>
      );
  }
}

export default function Project() {
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Dynamically inject Event JSON-LD for AI / search engines.
  useEffect(() => {
    if (!project) return undefined;
    const eventLd = {
      "@context": "https://schema.org",
      "@type": "Event",
      name: project.title,
      startDate: "2025-11-01",
      eventAttendanceMode: "https://schema.org/OfflineEventAttendanceMode",
      eventStatus: "https://schema.org/EventScheduled",
      location: {
        "@type": "Place",
        name: project.venue,
        address: {
          "@type": "PostalAddress",
          addressLocality: project.city,
          addressCountry: "IN",
        },
      },
      image: project.gallery?.slice(0, 3).map((it) => (typeof it === "string" ? it : it.src)),
      description: project.summary,
      organizer: { "@type": "Organization", name: "Design Mumbai" },
      performer: {
        "@type": "Organization",
        name: "Indoor Greens",
        url: "https://www.indoorgreens.in/",
      },
    };
    const el = document.createElement("script");
    el.type = "application/ld+json";
    el.setAttribute("data-project", project.slug);
    el.text = JSON.stringify(eventLd);
    document.head.appendChild(el);

    const prevTitle = document.title;
    document.title = `${project.title} — ${project.role} · Indoor Greens`;
    const metaDesc = document.querySelector('meta[name="description"]');
    const prevDesc = metaDesc?.getAttribute("content");
    if (metaDesc) metaDesc.setAttribute("content", project.summary);

    return () => {
      el.remove();
      document.title = prevTitle;
      if (metaDesc && prevDesc) metaDesc.setAttribute("content", prevDesc);
    };
  }, [project]);

  if (!project) return <Navigate to="/" replace />;

  const others = PROJECTS.filter((p) => p.slug !== project.slug).slice(0, 2);

  return (
    <main
      data-testid="project-page"
      className="bg-[#F9F8F6] text-[#2D332B] min-h-screen"
    >
      <Nav />

      {/* Hero */}
      <section className="pt-40 pb-14 sm:pt-48 sm:pb-16">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
          <Link
            to="/"
            data-testid="project-back-link"
            className="inline-flex items-center gap-2 text-[11px] tracking-[0.3em] uppercase text-[#5C6658] hover:text-[#2D332B] transition-colors"
          >
            <ArrowLeft className="w-3.5 h-3.5" /> Back to studio
          </Link>

          <p className="mt-10 text-[11px] tracking-[0.4em] uppercase text-[#5C6658]">
            Event · {project.year}
          </p>
          <h1
            data-testid="project-title"
            className="mt-4 font-[Outfit] font-extralight text-[#2D332B] text-[44px] sm:text-[72px] lg:text-[84px] leading-[0.98] tracking-[-0.02em]"
          >
            {project.title}
          </h1>
          <p className="mt-6 font-[Outfit] italic font-light text-[22px] sm:text-[28px] text-[#8A9A86] leading-[1.25]">
            {project.role}
          </p>

          <div className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-4 text-[11px] tracking-[0.25em] uppercase text-[#5C6658]">
            <span className="inline-flex items-center gap-2">
              <MapPin className="w-3.5 h-3.5" /> {project.venue}
            </span>
            <span className="inline-flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5" /> {project.date_label}
            </span>
          </div>
        </div>
      </section>

      {/* Cover */}
      {project.cover && (
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10">
          <div className="relative aspect-[4/3] sm:aspect-[3/2] overflow-hidden rounded-sm">
            <img
              src={project.cover}
              alt={project.title}
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      )}

      {/* Body + credits */}
      <section className="py-16 sm:py-24">
        <div className="max-w-[1200px] mx-auto px-6 sm:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-8">
            <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658] mb-4">
              Overview
            </p>
            <p className="font-[Outfit] font-light text-[22px] sm:text-[26px] leading-[1.4] text-[#2D332B]">
              {project.summary}
            </p>
            <div className="mt-4">
              {project.body?.map((b, i) => (
                <Block key={i} block={b} />
              ))}
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="bg-[#F2EFEB] border border-[#E5E1DB] rounded-sm p-8">
              <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                Credits
              </p>
              <dl className="mt-6 space-y-5">
                {project.credits.map((c) => (
                  <div key={c.label}>
                    <dt className="text-[10px] tracking-[0.25em] uppercase text-[#5C6658]">
                      {c.label}
                    </dt>
                    <dd className="mt-1.5 font-[Outfit] font-light text-[17px] text-[#2D332B] leading-[1.35]">
                      {c.value}
                    </dd>
                  </div>
                ))}
              </dl>

              {project.external_album_url && (
                <a
                  href={project.external_album_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="project-album-link"
                  className="mt-8 w-full inline-flex items-center justify-center gap-2 px-5 py-3.5 bg-[#2D332B] text-[#F9F8F6] text-[11px] tracking-[0.3em] uppercase hover:bg-[#8A9A86] transition-colors duration-500 rounded-sm"
                >
                  View full album <ArrowUpRight className="w-3.5 h-3.5" />
                </a>
              )}
            </div>
          </aside>
        </div>
      </section>

      {/* Gallery */}
      {project.gallery?.length > 0 && (
        <section className="pb-24">
          <div className="max-w-[1400px] mx-auto px-6 sm:px-10 lg:px-16">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C6658] mb-8">
              Gallery
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6 sm:gap-8">
              {project.gallery.map((item, i) => {
                const src = typeof item === "string" ? item : item.src;
                const caption = typeof item === "string" ? null : item.caption;
                return (
                  <figure
                    key={i}
                    data-testid={`project-gallery-${i}`}
                    className="flex flex-col"
                  >
                    <div className="relative overflow-hidden rounded-sm bg-[#F2EFEB]">
                      <img
                        src={src}
                        alt={caption || `${project.title} — image ${i + 1}`}
                        className="w-full h-auto block"
                        loading={i > 2 ? "lazy" : "eager"}
                      />
                    </div>
                    {caption && (
                      <figcaption
                        data-testid={`project-gallery-caption-${i}`}
                        className="mt-4 text-[13px] sm:text-[14px] leading-[1.65] italic text-[#5C6658] max-w-[42ch]"
                      >
                        {caption}
                      </figcaption>
                    )}
                  </figure>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-20 sm:py-24 border-t border-[#E5E1DB] bg-[#F2EFEB]">
        <div className="max-w-[1100px] mx-auto px-6 sm:px-10 text-center">
          <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C6658] mb-6">
            Work with us
          </p>
          <h2 className="font-[Outfit] font-extralight text-[#2D332B] text-[32px] sm:text-[46px] leading-[1.05] tracking-[-0.015em] max-w-2xl mx-auto">
            Planning an event, showroom or space that could
            <span className="italic text-[#8A9A86]"> breathe better</span>?
          </h2>
          <Link
            to="/#contact"
            className="mt-10 inline-flex items-center px-8 py-3.5 bg-[#2D332B] text-[#F9F8F6] text-[11px] tracking-[0.3em] uppercase hover:bg-[#8A9A86] transition-colors duration-500 rounded-sm"
          >
            Begin a conversation
          </Link>
        </div>
      </section>

      {/* Other projects (empty for now) */}
      {others.length > 0 && (
        <section className="py-20 border-t border-[#E5E1DB]">
          <div className="max-w-[1100px] mx-auto px-6 sm:px-10">
            <p className="text-[11px] tracking-[0.4em] uppercase text-[#5C6658] mb-10">
              Also in the studio
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {others.map((p) => (
                <Link
                  key={p.slug}
                  to={`/projects/${p.slug}`}
                  className="group block border border-[#E5E1DB] rounded-sm overflow-hidden hover:border-[#8A9A86] transition-colors"
                >
                  <div className="aspect-[16/9] overflow-hidden">
                    <img
                      src={p.cover}
                      alt={p.title}
                      className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-[1200ms]"
                    />
                  </div>
                  <div className="p-6">
                    <p className="text-[10px] tracking-[0.3em] uppercase text-[#5C6658]">
                      {p.year} · {p.role}
                    </p>
                    <h3 className="mt-3 font-[Outfit] font-light text-[22px] text-[#2D332B] leading-[1.15]">
                      {p.title}
                    </h3>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      <Footer />
    </main>
  );
}
